/**
 * ButtonGroup — Groups related buttons into a single visual unit with shared edges.
 *
 * ButtonGroup Component Guidelines
 * - Use when 2-3 actions are closely related and belong together visually (e.g.
 *   segmented controls, formatting toolbars, split buttons). Prefer separate
 *   buttons when actions are independent.
 * - Use `ButtonGroupSeparator` to add a visual divider between items when you want
 *   to visually distinguish segments without breaking the merged-edge layout.
 * - Use `ButtonGroupText` for non-interactive, display-only segments within the
 *   group (e.g. a static label or value between two buttons).
 * - Use `orientation="vertical"` for stacked button groups (e.g. sidebar action lists).
 * @see https://base-ui.com/react/components/button Base UI Button documentation
 * @see https://ui.shadcn.com/docs/components/button shadcn/ui Button documentation
 */
import type { LayoutClassName } from "@replo/design-system/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import React from "react";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { Separator } from "@replo/design-system/components/separator/Separator";
import twMerge from "@replo/design-system/utils/twMerge";
import { cva } from "class-variance-authority";

const buttonGroupVariants = cva(
  "has-[>[data-slot=button-group]]:gap-2 has-[select[aria-hidden=true]:last-child]:[&>[data-slot=select-trigger]:last-of-type]:rounded-r-md flex w-fit items-stretch [&>*]:focus-visible:z-10 [&>*]:focus-visible:relative [&>[data-slot=select-trigger]:not([class*='w-'])]:w-fit [&>input]:flex-1",
  {
    variants: {
      orientation: {
        horizontal:
          "[&>[data-slot]:not(:has(~[data-slot]))]:rounded-r-md! [&>[data-slot]~[data-slot]]:rounded-l-none [&>[data-slot]~[data-slot]]:border-l-0 [&>[data-slot]]:rounded-r-none",
        vertical:
          "[&>[data-slot]:not(:has(~[data-slot]))]:rounded-b-md! flex-col [&>*]:w-full [&>[data-slot]~[data-slot]]:rounded-t-none [&>[data-slot]~[data-slot]]:border-t-0 [&>[data-slot]]:rounded-b-none",
      },
    },
    defaultVariants: {
      orientation: "horizontal",
    },
  },
);

interface ButtonGroupProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className">,
    VariantProps<typeof buttonGroupVariants> {
  /**
   * className with type safety for layout classes only
   */
  className?: LayoutClassName<ClassName>;
  /**
   * flexible className marked as UNSAFE to indicate it is circumventing the design system
   */
  UNSAFE_className?: string;
}

function ButtonGroup<ClassName extends string>({
  className,
  UNSAFE_className,
  orientation,
  ...props
}: ButtonGroupProps<ClassName>) {
  return (
    <div
      role="group"
      data-slot="button-group"
      data-orientation={orientation}
      className={twMerge(
        buttonGroupVariants({ orientation }),
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function ButtonGroupText({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">) {
  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(
      {
        className: twMerge(
          "bg-sheet gap-2 rounded-md border border-border border-0.5 px-2.5 text-sm font-medium shadow-xs [&_svg:not([class*='size-'])]:size-4 flex items-center [&_svg]:pointer-events-none",
          className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "button-group-text",
    },
  });
}

function ButtonGroupSeparator({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="button-group-separator"
      orientation={orientation}
      className={twMerge(
        "bg-input relative self-stretch data-[orientation=horizontal]:mx-px data-[orientation=horizontal]:w-auto data-[orientation=vertical]:my-px data-[orientation=vertical]:h-auto",
        className as string,
      )}
      {...props}
    />
  );
}

export { ButtonGroup, ButtonGroupSeparator, ButtonGroupText };
